import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import ArbonicsDialog from "../comps/ArbonicsDialog/ArbonicsDialog";
import { sendEvent } from "../../../api";
import LockedInfoBanner from "./components/LockedInfoBanner";
import { Input, Text, Icon, Button, Sheet } from "@ui-component-library";
import { ErrorModal } from "../../../modals";
import { useMetricData } from "./hooks/UseMetricData";

import Skeleton from "../comps/Skeleton";
import WidgetWrapper from "../comps/WidgetWrapper";
import PropSelector from "../comps/PropSelector";

import LineChartWidget from "./widgets/LineChartWidget";
import TableWidget from "./widgets/TableWidget";
import MetricAreaWidget from "./widgets/MetricAreaWidget";
import MetricVolumeWidget from "./widgets/MetricVolumeWidget";
import BarChartWidget from "./widgets/BarGraphWidget";
import KeyDataWidget from "./widgets/KeyDataWidget";
import OpportunitiesWidget from "./widgets/OpportunitiesWidget";

import { usePropsDataStore } from "../store/PropsDataStore";
import SectionBadDataBanner from "./components/SectionBadDataBanner";
import { useDemoMinskogStore } from "../store/DemoMinskogStore";
import { DEMO_PROMPTS } from "../constants/demoPrompts";
import { useNavigationStore } from "../store/NavigationStore";

const PropselectorContainer = styled.div`
  width: 90vw;
  max-width: 450px;
  top: 10px;
`;

export const baseSkeletonSetup = () => {
  return (
    <div className="flex flex-col space-y-3 p-4">
      <Skeleton className="h-[125px] w-full rounded-xl" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-[200px] max-w-full" />
        <Skeleton className="h-4 w-[250px] max-w-full mb-6" />
        <Skeleton className="h-4 w-[200px] max-w-full" />
        <Skeleton className="h-4 w-[250px] max-w-full" />
        <Skeleton className="h-4 w-[225px] max-w-full" />
      </div>
    </div>
  );
};

export default function Overblick() {
  const {
    loading,
    totalVolume,
    avgGrowth,
    updateTotalVolume,
    updateAvgGrowth,
    totalActiveProps,
    gotoSbpPdf,
  } = useMetricData();

  const { props } = usePropsDataStore()

  const activeProps = props.filter(p => p.active && p?.data)

  const singleActiveProp = activeProps.length === 1 ? activeProps[0] : null;
  const canEditActive = singleActiveProp
    ? (singleActiveProp.role ? singleActiveProp.role !== 'view' : !singleActiveProp.is_shared)
    : true;

  const has_fucked = activeProps.some((prop) => prop.data.sections.some((s) => s.is_fucked));

  const {DemoBlockFunctionWrapper} = useDemoMinskogStore()

  const [isArbonicsDialogVisible, setIsArbonicsDialogVisible] =
    useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [editData, setEditData] = useState({
    totalVolume: undefined,
    avgGrowth: undefined,
  });

  const tableWidgetRef = useRef(null);
  /** Avoid repeating scroll for the same `view.section` until it changes. */
  const lastScrolledForSectionRef = useRef(null);

  const { view } = useNavigationStore();

  useEffect(() => {
    if (!view.section) {
      lastScrolledForSectionRef.current = null;
      return;
    }
    if (totalActiveProps <= 0) return;
    if (lastScrolledForSectionRef.current === view.section) return;

    const id = requestAnimationFrame(() => {
      const el = tableWidgetRef.current;
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      lastScrolledForSectionRef.current = view.section;
    });

    return () => cancelAnimationFrame(id);
  }, [view.section, totalActiveProps, loading]);

  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem(
      "overblickScrollPosition"
    );
    if (loading || !savedScrollPosition) return;

    const position = parseInt(savedScrollPosition, 10);
    sessionStorage.removeItem("overblickScrollPosition");

    setTimeout(() => {
      const mainContainer = document.getElementById("mainContainer");
      if (mainContainer) {
        mainContainer.scrollTo({
          top: position,
          behavior: "smooth",
        });
      }
    }, 300);
  }, [loading]);

  const formatValueForDisplay = (value, field) => {
    if (value === null || value === undefined || typeof value !== "number") {
      return "";
    }

    if (field === "avgGrowth") {
      return value.toFixed(1).replace(".", ",");
    }
    return Math.round(value).toString();
  };

  const handleInputChange = (field) => (value) => {
    let sanitized = String(value);

    if (field === "avgGrowth") {
      // For avgGrowth we allow comma for decimal
      sanitized = sanitized.replace(".", ",");
    } else {
      // For totalVolume we remove any decimal separators
      sanitized = sanitized.replace(/[.,]/g, "");
    }

    setEditData({ ...editData, [field]: sanitized });
  };

  const normalizeValue = (value, fallback) => {
    if (value === undefined || value === null || value === "") {
      return fallback;
    }
    const normalized = String(value).replace(",", ".");
    return parseFloat(normalized);
  };

  const handleArbonicsClick = () => {
    setIsArbonicsDialogVisible(true);
    sendEvent("co2_open_modal", { source: "overblick" });
    gtag("event", "co2_open_modal", { method: "site" });
  };

  const editKeyValueContent = {
    title: "Ändra",
    submitLabel: "Spara",
    content: (isLoading) => (
      <>
        <div className="mb-2">
          <Input
            debounceDelay={0}
            variant="fluid"
            className="w-full"
            label="Totalvolym"
            value={
              editData?.totalVolume ??
              formatValueForDisplay(totalVolume, "totalVolume")
            }
            onChange={handleInputChange("totalVolume")}
            suffix="m³sk"
            disabled={isLoading}
          />
        </div>
        <div>
          <Input
            debounceDelay={0}
            variant="fluid"
            className="w-full"
            label="Medelbonitet"
            value={
              editData?.avgGrowth ??
              formatValueForDisplay(avgGrowth, "avgGrowth")
            }
            onChange={handleInputChange("avgGrowth")}
            suffix="m³sk/ha/år"
            disabled={isLoading}
          />
        </div>
      </>
    ),
    onSubmit: async () => {
      DemoBlockFunctionWrapper(async () => {
        const avgGrowthValue = normalizeValue(editData.avgGrowth, avgGrowth);
        const totalVolumeValue = normalizeValue(
          editData.totalVolume,
          totalVolume
        );

        await Promise.all([
          updateAvgGrowth(avgGrowthValue),
          updateTotalVolume(totalVolumeValue),
        ]);
      }, DEMO_PROMPTS.UPDATE_VOLUME_AND_GROWTH)
      
    },
  };

  const editVolumeContent = {
    title: "Ändra",
    submitLabel: "Spara",
    content: (isLoading) => (
      <div className="mb-2">
        <Input
          debounceDelay={0}
          variant="fluid"
          className="w-full"
          label="Totalvolym"
          value={
            editData?.totalVolume ??
            formatValueForDisplay(totalVolume, "totalVolume")
          }
          onChange={handleInputChange("totalVolume")}
          suffix="m³sk"
          disabled={isLoading}
        />
      </div>
    ),
    onSubmit: async () => {
      DemoBlockFunctionWrapper(async () => {
      const totalVolumeValue = normalizeValue(
        editData.totalVolume,
        totalVolume
      );
      await Promise.all([updateTotalVolume(totalVolumeValue)]);
    }, DEMO_PROMPTS.UPDATE_FOREST_PLAN);
    },
  };

  return (
    <div
      className="relative bg-cloud-50"
      style={{ minHeight: "100dvh" }}
    >

      <>
        <div className="@container w-full min-w-0">
          {window.location.pathname.includes('sudo') && <div className="sticky top-0 z-[1040] sm:ml-8 lg:ml-52 mt-4"><Button variant="primary" onClick={(e) => gotoSbpPdf(e)}>Hämta Pdf</Button></div>}

          {!has_fucked && (
            <LockedInfoBanner className="w-full max-w-[1240px] mx-auto" />
          )}

          {has_fucked && (
            <SectionBadDataBanner className="w-full max-w-[1240px] mx-auto" />
          )}

          <div className="grid w-full min-w-0 grid-cols-[repeat(1,minmax(0,456px))] @md:grid-cols-[repeat(2,minmax(0,456px))] @xl:grid-cols-[repeat(3,minmax(0,456px))] auto-rows-[434px] gap-4 justify-center max-w-[1240px] px-4 @md:px-8 pt-4 mx-auto mb-8">
            <WidgetWrapper
              title="Areal"
              infoContent={
                <div className="flex flex-col gap-8">
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/normal">
                      Areal avser den sammanlagda markytan inom fastighetens
                      gränser, vanligtvis angiven i hektar (ha). I
                      skogsbruksplanen delas arealen in i olika marktyper,
                      exempelvis:
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/normal">
                      <b>Skogsmark:</b> mark som används för virkesproduktion
                      och har förutsättningar för trädens tillväxt.
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      <b>Övrig mark:</b> mark som inte räknas som produktiv
                      skogsmark, till exempel impediment, betesmarker, vägar
                      eller bebyggelse.
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      <b>Vatten:</b> sjöar, myrar eller andra vattenytor som
                      ingår i fastigheten.
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Arealuppdelningen ger en grundläggande bild av
                      fastighetens sammansättning och används som utgångspunkt
                      för planering, beräkning av virkesförråd och värdering av
                      skogen.
                    </Text>
                  </div>
                </div>
              }
            >
              {loading ? (
                baseSkeletonSetup()
              ) : totalActiveProps > 0 ? (
                <MetricAreaWidget />
              ) : (
                baseSkeletonSetup()
              )}
            </WidgetWrapper>

            <WidgetWrapper
              title="Virkesförråd"
              infoContent={
                <div className="flex flex-col gap-8">
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/normal">
                      I en skogsbruksplan avser virkesförråd den totala mängden
                      virke som finns stående i skogen vid
                      inventeringstillfället. Det uttrycks i kubikmeter skog
                      (m³sk), vilket motsvarar den fasta volymen av stamved ovan
                      stubben, alltså den del av träden som kan utnyttjas vid
                      avverkning.
                    </Text>
                  </div>

                  <Text variant="text-sm/leading-normal/normal">
                    Virkesförrådet visar hur mycket biologiskt och ekonomiskt
                    värde som för närvarande är bundet i skogen och är därmed en
                    av planens mest centrala uppgifter. Det ger en grund för att
                    bedöma avverkningspotential, tillväxt och värdeutveckling
                    över tid.
                  </Text>
                  <Text variant="text-sm/leading-normal/normal">
                    På Treebula anger vi virkesförrådet både utifrån
                    trädslagsfördelning och huggningsklasser.
                  </Text>
                </div>
              }
              {...(totalActiveProps === 1 && canEditActive
                ? { editDialog: editVolumeContent }
                : {})}
            >
              {loading ? baseSkeletonSetup() : <MetricVolumeWidget />}
            </WidgetWrapper>
            <WidgetWrapper
              title="Nyckeldata"
              infoContent={
                <div className="flex flex-col gap-8">
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Totalvolym
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Ett nyckeltal som visar <i>total volym</i> för den
                      sammanlagda mängden virke som finns i skogen, oavsett
                      trädslag eller åldersklass. Totalvolymen omfattar både
                      slutavverkningsbar skog och yngre bestånd.
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Fastighetsvärde
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Avser ett uppskattat marknadsvärde för skogsfastigheten
                      som bygger på senaste försäljningsstatistiken från Ludvig
                      & Co och Svefa, kombinerat med fastighetens virkesförråd.
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Virkesförråd medeltal
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Avser genomsnittlig virkesvolym per hektar produktiv
                      skogsmark inom fastigheten, uttryckt i m³sk/ha (kubikmeter
                      stammar på rot per hektar skogsmark).
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Medelbonitet
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Avser ett mått på markens produktionsförmåga, uttryckt i
                      m³sk per hektar och år (m³sk/ha/år).
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Beräkning:
                      <br />
                      Medelboniteten beräknas som ett vägt medelvärde av
                      boniteten för fastighetens olika bestånd. Den anger hur
                      mycket virke marken i genomsnitt kan producera per hektar
                      och år under normala förhållanden.
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Tillväxt
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Avser den årliga ökningen av virkesvolym i skogen,
                      uttryckt i m³sk per år (kubikmeter stammar på rot per år).
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Värdetillväxt
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Avser den årliga ekonomiska tillväxten i skogens värde,
                      uttryckt i kronor per år (kr/år).
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Beräkning:
                      <br />
                      Den beräknas genom att multiplicera den årliga tillväxten
                      (m³sk/år) med det aktuella marknadspriset per m³sk,
                      baserat på senaste försäljningsstatistik från Ludvig & Co
                      och Svefa.
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Årlig tillväxt
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Avser den årliga ökningen av virkesvolym i skogen,
                      uttryckt i m³sk per år (kubikmeter stammar på rot per år).
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Potential kolkrediter
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      En preliminär analys visar om det finns möjlighet att
                      förlänga omloppstiden på din slutavverkningsmogna skog och
                      därigenom tjäna kolkrediter. En djupare analys behövs för
                      att verkligen verifiera potentialen.
                    </Text>
                  </div>
                </div>
              }
              {...(totalActiveProps === 1 && canEditActive
                ? { editDialog: editKeyValueContent }
                : {})}
            >
              {loading ? baseSkeletonSetup() : <KeyDataWidget />}
            </WidgetWrapper>

            <WidgetWrapper
              title="Möjligheter"
              infoContent={
                <div className="flex flex-col gap-8">
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/normal">
                      Skogen rymmer många värden, både ekonomiska, miljömässiga
                      och rekreativa. Här hittar du konkreta sätt att{" "}
                      <b>utnyttja utnyttja din skogs potential</b> utifrån just
                      dina förutsättningar.
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Här kan du:
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      <b>Sälja biobränsle:</b> Ta del av alternativ för
                      brännved och GROT som kan ge ytterligare intäkter.
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      <b>Utforska kolkrediter:</b> Lär dig mer om hur din skog
                      kan bidra till klimatnytta och hur du kan delta i
                      kolkreditsprogram.
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      <b>Beställa drönarinventering:</b> Få en detaljerad analys
                      av din skog med högupplösta bilder och identifiering av
                      eventuella skogsskador, till exempel stormfällning eller
                      insektsangrepp.
                    </Text>
                  </div>
                </div>
              }
            >
              {loading ? (
                baseSkeletonSetup()
              ) : (
                <OpportunitiesWidget showDialog={handleArbonicsClick} />
              )}
            </WidgetWrapper>

            <WidgetWrapper
              title="Fastighetsvärde utv. (kr/m³sk)"
              infoContent={
                <Text variant="text-sm/leading-normal/normal">
                  Avser ett uppskattat marknadsvärde för skogsfastigheten som
                  bygger på senaste försäljningsstatistiken från Ludvig & Co och
                  Svefa, kombinerat med fastighetens virkesförråd.
                </Text>
              }
            >
              {loading ? baseSkeletonSetup() : <LineChartWidget />}
            </WidgetWrapper>

            <WidgetWrapper
              title="Åldersfördelning"
              infoContent={
                <div className="flex flex-col gap-8">
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/normal">
                      Åldersfördelningen visar hur skogens bestånd är fördelade
                      i olika åldersintervall och presenteras i skogsbruksplanen
                      på två sätt:
                    </Text>

                    <Text variant="text-sm/leading-normal/normal">
                      <b>Arealfördelning per åldersintervall:</b> visar hur stor
                      del av den produktiva skogsmarken (i hektar) som tillhör
                      varje åldersklass. Den beskriver alltså hur mycket mark
                      som upptas av ungskog, gallringsskog och äldre skog.
                    </Text>

                    <Text variant="text-sm/leading-normal/normal">
                      <b>Volymfördelning per åldersintervall:</b> visar hur stor
                      andel av det totala virkesförrådet (i m³sk) som finns i
                      respektive åldersklass.
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text variant="text-sm/leading-normal/semibold">
                      Skillnaden mellan areal och volym
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Skillnaden mellan dessa två sätt att visa
                      åldersfördelningen är central:
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Arealfördelningen speglar skogens struktur, hur stor del
                      av marken som befinner sig i olika utvecklingsstadier.
                    </Text>
                    <Text variant="text-sm/leading-normal/normal">
                      Volymfördelningen visar istället var värdet och
                      virkesmassan finns, eftersom äldre bestånd innehåller
                      betydligt mer virke per hektar än yngre.
                    </Text>
                  </div>
                  <Text variant="text-sm/leading-normal/normal">
                    I praktiken innebär det att unga skogar ofta upptar en
                    större andel av arealen, medan äldre bestånd dominerar
                    volymen. Tillsammans ger de två fördelningarna en
                    helhetsbild av skogens åldersstruktur, produktion och
                    framtida avverkningsmöjligheter.
                  </Text>
                </div>
              }
            >
              {loading ? baseSkeletonSetup() : <BarChartWidget />}
            </WidgetWrapper>

            {totalActiveProps > 0 && (
              <TableWidget ref={tableWidgetRef} />
            )}
          </div>
        </div>
      </>
      <ArbonicsDialog
        visible={isArbonicsDialogVisible}
        hide={() => {
          setIsArbonicsDialogVisible(false);
        }}
      />

      {/* ERROR Modal */}
      <ErrorModal
        show={errorModal}
        title={errorModal?.title}
        message={errorModal?.message}
        onToggle={() => setErrorModal(false)}
      />
    </div>
  );
}
